import { type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { CopyContent } from '../CopyContent/CopyContent';
import { cn } from '../lib/utils';

const copyableFieldVariants = tv({
  slots: {
    root: 'flex min-w-0 flex-col gap-0.5',
    label: 'text-xs text-uniform-gray-500 dark:text-uniform-gray-400',
    value: 'text-sm font-medium text-uniform-gray-900 dark:text-uniform-gray-100',
    empty: 'text-sm text-uniform-gray-400 dark:text-uniform-gray-500'
  },
  variants: {
    mono: {
      true: { value: 'break-all font-mono text-[12.5px]' }
    }
  }
});

type CopyableFieldVariants = VariantProps<typeof copyableFieldVariants>;

interface CopyableFieldProps {
  label: ReactNode;
  /** Shown and copied; no copy button renders when empty. */
  value?: string | null;
  mono?: boolean;
  emptyText?: string;
  className?: string;
}

function CopyableField({
  label,
  value,
  mono = false,
  emptyText = '—',
  className
}: CopyableFieldProps) {
  const { root, label: labelCls, value: valueCls, empty } = copyableFieldVariants({ mono });

  return (
    <div className={cn(root(), className)}>
      <span className={labelCls()}>{label}</span>
      {value ? (
        <CopyContent content={value}>
          <span className={valueCls()}>{value}</span>
        </CopyContent>
      ) : (
        <span className={empty()}>{emptyText}</span>
      )}
    </div>
  );
}

export { CopyableField, copyableFieldVariants };
export type { CopyableFieldProps, CopyableFieldVariants };
